import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ADMIN_PREFIX, SuiteApiClient } from "@weco/suite-client";
import { readModuleAccess } from "../lib/moduleAccess";

const api = new SuiteApiClient(API_ADMIN_PREFIX);

/**
 * Where Paymob sends the browser after checkout.
 *
 * It has to work without knowing which organization paid. Paymob's dashboard
 * holds **one** redirect URL for every payment through that integration, so an
 * organization id cannot be baked into it — the id is passed per payment, and
 * this page treats it as a hint it can do without.
 *
 * It also deliberately does not decide whether the payment succeeded. Paymob
 * appends its result to this URL, and a URL the customer's own browser follows
 * can be edited. The statement's status comes from the server, which only
 * moves it on a signed callback; until that arrives the honest answer is "being
 * confirmed", not a green tick.
 *
 * It only sends people to the organization's billing screen in the admin
 * console when they can open it (a super admin, or an Owner holding
 * `organizations:manage` with RBAC on). Anyone else who paid — an Admin from
 * the in-app banner, or an Owner before RBAC is rolled out — would land on a
 * "forbidden" page straight after paying, so they go home instead, where the
 * billing banner (or the paused screen) shows the bill's new state.
 */
export const PaymentReturnPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const orgHint = params.get("org") || "";
  // Back from saving a card (`&card=1`) rather than paying a bill.
  const savedCard = params.get("card") === "1";
  const [message, setMessage] = useState(savedCard ? "Confirming your card…" : "Confirming your payment…");

  useEffect(() => {
    let cancelled = false;

    const resolveDestination = async (): Promise<string> => {
      const access = readModuleAccess();
      const org = orgHint || access.orgId || "";
      const canOpenOrgBilling =
        access.isSuperAdmin ||
        (access.rbacEnabled && !!org && (access.orgScopes[org] ?? []).includes("organizations:manage"));
      if (!canOpenOrgBilling) return "/";
      if (orgHint) return `/people/organizations/${orgHint}/billing`;
      // No hint: fall back to whatever this person administers. One org goes
      // straight there; several, or none, land on the picker rather than
      // guessing wrong.
      try {
        const res = await api.get<{ results?: Array<{ id: string }> }>(
          "/organizations/?page_size=2",
        );
        const orgs = res.results ?? [];
        if (orgs.length === 1) return `/people/organizations/${orgs[0].id}/billing`;
      } catch {
        // Falls through to the picker — this page is a signpost, not a gate.
      }
      return "/people/my-organizations";
    };

    // A beat before redirecting: the signed callback and this redirect race,
    // and arriving to a bill that still says "Due" reads as a failed payment.
    const run = async () => {
      const destination = await resolveDestination();
      if (cancelled) return;
      const done = savedCard ? "Card received." : "Payment received.";
      setMessage(destination === "/" ? `${done} Taking you back…` : `${done} Taking you back to billing…`);
      window.setTimeout(() => {
        if (!cancelled) navigate(destination, { replace: true });
      }, 1500);
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [navigate, orgHint, savedCard]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--color-background)",
        color: "var(--text-primary)",
        padding: 24,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div
          style={{
            width: 48,
            height: 48,
            margin: "0 auto 16px",
            borderRadius: "50%",
            border: "3px solid var(--border)",
            borderTopColor: "var(--accent)",
            animation: "spin 0.9s linear infinite",
          }}
        />
        <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{message}</p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 8 }}>
          {savedCard
            ? "Paymob confirms your card to us directly. If the old card still shows, give it a moment and refresh."
            : "Your bank confirms the payment to us directly. If the statement still shows as due, give it a moment and refresh."}
        </p>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    </div>
  );
};

export default PaymentReturnPage;
